import { useState } from "react";
import { Topbar } from "../components/Topbar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { StatusBadge } from "../components/StatusBadge";
import { USERS, REQUIREMENT_TYPES, JURISDICTIONS } from "../data/mockData";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import { Building, Users2, ScrollText, Globe2, Plus, Eye } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const SECTIONS = [
  { id: "organisation", label: "Organisation", icon: Building },
  { id: "users", label: "Users", icon: Users2 },
  { id: "requirement-types", label: "Requirement types", icon: ScrollText },
  { id: "jurisdictions", label: "Jurisdictions", icon: Globe2 },
];

export default function Settings() {
  const [section, setSection] = useState("organisation");
  const { isAdmin } = useAuth();

  return (
    <>
      <Topbar title="Settings" subtitle={isAdmin ? "Prototype — form actions are simulated" : "Read-only view · client role"} />

      <div className="px-6 lg:px-8 py-8">
        {!isAdmin && (
          <div
            data-testid="settings-readonly-banner"
            className="mb-6 flex items-center gap-2 rounded-sm border border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10 px-4 py-2.5 text-xs text-sky-800 dark:text-sky-300"
          >
            <Eye size={13} />
            You're viewing settings as a client. Editing is restricted to administrators.
          </div>
        )}
        <div className="grid grid-cols-12 gap-6">
          {/* Side nav */}
          <nav
            aria-label="Settings sections"
            className="col-span-12 md:col-span-3 xl:col-span-2 space-y-1"
            data-testid="settings-nav"
          >
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                data-testid={`settings-nav-${s.id}`}
                onClick={() => setSection(s.id)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm transition-colors",
                  section === s.id
                    ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                    : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                )}
              >
                <s.icon size={14} /> {s.label}
              </button>
            ))}
          </nav>

          {/* Content */}
          <div className="col-span-12 md:col-span-9 xl:col-span-10">
            {section === "organisation" && <OrganisationSection isAdmin={isAdmin} />}
            {section === "users" && <UsersSection isAdmin={isAdmin} />}
            {section === "requirement-types" && <RequirementTypesSection isAdmin={isAdmin} />}
            {section === "jurisdictions" && <JurisdictionsSection isAdmin={isAdmin} />}
          </div>
        </div>
      </div>
    </>
  );
}

function Panel({ title, description, children, action }) {
  return (
    <section className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
      <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-slate-200 dark:border-slate-800">
        <div>
          <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-50">
            {title}
          </h2>
          {description && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{description}</p>
          )}
        </div>
        {action}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function OrganisationSection({ isAdmin }) {
  const [emailNotifs, setEmailNotifs] = useState(true);
  return (
    <Panel
      title="Organisation profile"
      description="Change how Factor is displayed to your team and partners"
      action={
        isAdmin ? (
          <Button
            data-testid="save-org-btn"
            onClick={() => toast.success("Organisation profile saved (simulated)")}
            className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white text-white h-9"
          >
            Save changes
          </Button>
        ) : null
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500">
            Organisation name
          </Label>
          <Input defaultValue="Factor Operations" className="mt-1.5" data-testid="org-name-input" disabled={!isAdmin} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500">
            Contact email
          </Label>
          <Input defaultValue="ops@factor.ops" className="mt-1.5" data-testid="org-email-input" disabled={!isAdmin} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500">
            Registered address
          </Label>
          <Input
            defaultValue="Passeig del Born 8, Palma"
            className="mt-1.5"
            data-testid="org-address-input"
            disabled={!isAdmin}
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500">Tax ID</Label>
          <Input defaultValue="B12345678" className="mt-1.5" data-testid="org-tax-input" disabled={!isAdmin} />
        </div>
        <div className="lg:col-span-2 flex items-center justify-between rounded-sm border border-slate-200 dark:border-slate-800 px-4 py-3">
          <div>
            <p className="text-sm text-slate-900 dark:text-slate-100">Renewal email alerts</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Send digest of expiring requirements to operators every Monday.
            </p>
          </div>
          <Switch
            data-testid="org-notifs-toggle"
            checked={emailNotifs}
            onCheckedChange={setEmailNotifs}
            disabled={!isAdmin}
          />
        </div>
      </div>
    </Panel>
  );
}

function UsersSection({ isAdmin }) {
  return (
    <Panel
      title="Team members"
      description="Manage who can access this workspace"
      action={
        isAdmin ? (
          <Button
            data-testid="invite-user-btn"
            onClick={() => toast.success("Invitation sent (simulated)")}
            className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white text-white h-9 gap-1"
          >
            <Plus size={14} /> Invite user
          </Button>
        ) : null
      }
    >
      <div className="overflow-x-auto -mx-6">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-800/40 text-[11px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
              <th className="py-3 px-6 text-left">Name</th>
              <th className="py-3 px-4 text-left">Email</th>
              <th className="py-3 px-4 text-left">Role</th>
              <th className="py-3 px-4 text-left">Last active</th>
            </tr>
          </thead>
          <tbody>
            {USERS.map((u) => (
              <tr
                key={u.id}
                className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40"
              >
                <td className="py-3 px-6 text-slate-900 dark:text-slate-100 font-medium">
                  {u.name}
                </td>
                <td className="py-3 px-4 text-slate-700 dark:text-slate-300">{u.email}</td>
                <td className="py-3 px-4">
                  <StatusBadge
                    tone={
                      u.role === "Admin"
                        ? "green"
                        : u.role === "Operator"
                        ? "amber"
                        : u.role === "Owner"
                        ? "grey"
                        : "grey"
                    }
                    showDot={false}
                  >
                    {u.role}
                  </StatusBadge>
                </td>
                <td className="py-3 px-4 text-slate-500">{u.lastActive}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function RequirementTypesSection({ isAdmin }) {
  return (
    <Panel
      title="Requirement types"
      description="Definitions that structure the compliance workflow"
      action={
        isAdmin ? (
          <Button
            data-testid="add-req-type-btn"
            onClick={() => toast.success("New requirement type added (simulated)")}
            className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white text-white h-9 gap-1"
          >
            <Plus size={14} /> New type
          </Button>
        ) : null
      }
    >
      <ul className="divide-y divide-slate-200 dark:divide-slate-800 -mx-6">
        {REQUIREMENT_TYPES.map((r) => (
          <li
            key={r.id}
            className="flex items-center justify-between px-6 py-3"
            data-testid={`req-type-${r.id}`}
          >
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{r.name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{r.category}</p>
            </div>
            <StatusBadge tone="grey" showDot={false}>
              {r.category}
            </StatusBadge>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function JurisdictionsSection({ isAdmin }) {
  return (
    <Panel
      title="Jurisdictions"
      description="Registered regions where Factor tracks requirements"
      action={
        isAdmin ? (
          <Button
            data-testid="add-jurisdiction-btn"
            onClick={() => toast.success("Jurisdiction added (simulated)")}
            className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white text-white h-9 gap-1"
          >
            <Plus size={14} /> New jurisdiction
          </Button>
        ) : null
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {JURISDICTIONS.map((j) => (
          <div
            key={j.id}
            data-testid={`jurisdiction-${j.id}`}
            className="rounded-sm border border-slate-200 dark:border-slate-800 p-4"
          >
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{j.name}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{j.country}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
              {j.requirementCount} registered requirement types
            </p>
          </div>
        ))}
      </div>
    </Panel>
  );
}
